import React from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import ColoredButton from 'components/ColoredButton';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type {
  ActionLibraryInstance,
  UpdateActionLibraryInstancePayload,
} from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

type Props = {
  open: boolean;
  onClose: () => void;
  instance: ActionLibraryInstance;
  onSuccess: () => void;
};

const buildCompletePayload = (instance: ActionLibraryInstance, comment: string): UpdateActionLibraryInstancePayload => ({
  title: instance.title,
  deadline: instance.deadline,
  reminder: instance.reminder,
  delegatedTo: instance.delegatedTo,
  specialInstruction: instance.specialInstruction || '',
  steps: (instance.steps || []).map(s => ({ id: s.id, mandatory: s.mandatory, status: s.status })),
  assetId: instance.asset,
  status: 'completed',
  completionComment: comment || undefined,
});

const CompleteInstanceDialog: React.FC<Props> = ({ open, onClose, instance, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [comment, setComment] = React.useState('');

  const completeMutation = useMutation(
    (completionComment: string) =>
      actionLibraryInstancesApi.updateActionLibraryInstance(instance.id, buildCompletePayload(instance, completionComment)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
        enqueueSnackbar(t(action_library_messages.success.instance_updated), { variant: 'success' });
        onSuccess();
        setComment('');
        onClose();
      },
      onError: () => {
        enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
      },
    },
  );

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const handleSubmit = () => {
    completeMutation.mutate(comment);
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance_actions.complete_dialog_title)}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t(action_library_messages.instance_actions.complete_dialog_comment_placeholder)}
          multiline
          onChange={e => setComment(e.target.value)}
          rows={3}
          value={comment}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel[0], general_messages.cancel[1])}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={completeMutation.isLoading}
          icon={false}
          onClick={handleSubmit}
          showLoader={completeMutation.isLoading}
          textLike={false}
          variant='contained'
        >
          {t(action_library_messages.instance_actions.complete_instance)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteInstanceDialog;
